// nflverse stats_team_week_YYYY.csv → `team.stat.line` facts.
//
// What a club's defense and special teams did, one row per team per game. This
// is the file that fills a D/ST slot, and it sat unregistered beside
// `stats_player` for a week while the handoff said team defense had no source:
// nflverse builds it with `nflfastR::calculate_stats()` from its own
// play-by-play, so it is the same CC BY 4.0 grant as the player file, and its
// header (verified against the live 2025 asset on 2026-09-18) carries every
// counting column the sport's `dst_*` catalog needs.
//
// THE ASSET IS `stats_team_week`, for the same reason `stats.ts` picks the weekly
// player file: the release carries `_reg`, `_post` and `_regpost` season totals
// beside it, and the totals are derivable from the weeks where the weeks are
// not derivable from the totals.
//
// KEYS ARE OURS, exactly as in `stats.ts`, and written as literals for the same
// dependency reason. Two of the catalog's ten defensive keys are NOT the club's
// own row:
//
//   dst_yds_allowed  The OPPONENT's net yardage — `passing_yards +
//                    sack_yards_lost + rushing_yards` from the opponent's row in
//                    the same file. Both rows of a game are in one file, so it is
//                    derived here, the way `pass_inc` is derived in stats.ts.
//                    `sack_yards_lost` is negative in the file, so it is added.
//   dst_pts_allowed  The opponent's final score. This file has no points column;
//                    the projection joins `contest.result` on `gameId`, which we
//                    already ingest for every game (workers/ingest/src/stats.ts).
//
// ONE COLUMN IS A TRAP AND IS DELIBERATELY NOT READ. `pt_return_tds` sits in the
// punting block and counts return touchdowns scored AGAINST this club's punter.
// Return scores FOR the club are `special_teams_tds`, which is what `dst_ret_td`
// reads. A test pins it.
import { cell, num, parseCsv } from '../../format/csv';
import type { CandidateEntity, CandidateFact, ExtractResult } from '../../acquisition/adapter';
import { NS } from './namespaces';

/** Columns without which a row cannot be placed or its opponent found. */
const REQUIRED = ['season', 'week', 'team', 'season_type', 'game_id', 'opponent_team'] as const;

/** Our stat key ← their column, on the club's OWN row. */
const DIRECT: readonly (readonly [ourKey: string, column: string])[] = [
  ['dst_sack', 'def_sacks'],
  ['dst_int', 'def_interceptions'],
  ['dst_ff', 'def_fumbles_forced'],
  // Recoveries of the opponent's fumbles. `fumble_recovery_own` is the offense
  // picking up its own ball and is not a defensive stat.
  ['dst_fum_rec', 'fumble_recovery_opp'],
  ['dst_td', 'def_tds'],
  ['dst_safety', 'def_safeties'],
  // Kick and punt return scores FOR the club. Not `pt_return_tds` — see header.
  ['dst_ret_td', 'special_teams_tds'],
];

/** Keys that are a sum of several columns. The same sum `stats.ts` makes. */
const SUMMED: readonly (readonly [ourKey: string, columns: readonly string[]])[] = [
  ['dst_blk', ['def_punt_blocks', 'def_pat_blocks', 'def_fg_blocks']],
];

/** A club's net offensive yardage, which is what its OPPONENT's defense allowed. */
const NET_YARDS = ['passing_yards', 'sack_yards_lost', 'rushing_yards'] as const;

/** Every column a line is scored from. Checked against every file from 2020 to 2025 on 2026-10-31. */
const SCORED: readonly string[] = [
  ...DIRECT.map(([, column]) => column),
  ...SUMMED.flatMap(([, columns]) => columns),
  ...NET_YARDS,
];

export interface TeamStatLineOptions {
  observedAt: string;
}

export function extractTeamStatLines(text: string, opts: TeamStatLineOptions): ExtractResult {
  const table = parseCsv(text);

  for (const column of REQUIRED) {
    if (!table.header.includes(column)) {
      throw new Error(
        `nflverse team stats: required column "${column}" is missing. ` +
          `Header has ${table.header.length} columns: ${table.header.join(', ')}`,
      );
    }
  }
  // EVERY COLUMN A LINE IS SCORED FROM, as `stats.ts` requires its own: one
  // renamed and read as absent would score that stat 0 for every defense with
  // no alarm, and a yardage column gone would move every defense's tier.
  const gone = SCORED.filter((column) => !table.header.includes(column));
  if (gone.length > 0)
    throw new Error(
      `nflverse team stats: ${gone.length === 1 ? 'a column' : 'columns'} the scoring reads ${gone.length === 1 ? 'is' : 'are'} missing: ${gone.join(', ')}. ` +
        `Header has ${table.header.length} columns: ${table.header.join(', ')}`,
    );

  // FIRST PASS: every club's net yardage by game, so the second pass can read
  // the opponent's. A game's two rows are always in the same file, because the
  // file is a season.
  const netYards = new Map<string, number>();
  for (const row of table.rows) {
    const gameId = cell(row, 'game_id');
    const team = cell(row, 'team');
    if (gameId === null || team === null) continue;
    const parts = NET_YARDS.map((c) => num(row, c)).filter((v): v is number => v !== null);
    if (parts.length === 0) continue;
    netYards.set(
      `${gameId}|${team}`,
      parts.reduce((a, b) => a + b, 0),
    );
  }

  const entities = new Map<string, CandidateEntity>();
  const facts: CandidateFact[] = [];
  const skipped = new Map<string, number>();
  const weeks = new Set<string>();
  const emitted = new Set<string>();
  const skip = (reason: string) => skipped.set(reason, (skipped.get(reason) ?? 0) + 1);

  const assertTeam = (key: string) => {
    if (entities.has(`${NS.team}:${key}`)) return;
    entities.set(`${NS.team}:${key}`, {
      type: 'team',
      namespace: NS.team,
      key,
      display: key,
      otherKeys: {},
      evidence: { league: 'nfl' },
    });
  };

  for (const row of table.rows) {
    const team = cell(row, 'team');
    const season = num(row, 'season');
    const period = num(row, 'week');
    const seasonType = cell(row, 'season_type');
    const gameId = cell(row, 'game_id');
    const opponent = cell(row, 'opponent_team');

    if (team === null) {
      skip('no team');
      continue;
    }
    if (season === null || period === null) {
      skip('no season or week');
      continue;
    }

    // Postseason week 1 and regular-season week 1 are different weeks; the
    // grain carries the season type so they cannot collide. The trailing
    // separator on the scope is load-bearing for the same reason it is in
    // stats.ts: `2026|REG|1` is a prefix of `2026|REG|1x` for every x.
    const factKey = `${season}|${seasonType ?? 'REG'}|${period}|${team}`;
    if (emitted.has(factKey)) {
      skip('duplicate team-week in one file');
      continue;
    }
    emitted.add(factKey);
    weeks.add(`${season}|${seasonType ?? 'REG'}|${period}|`);

    // ZEROS ARE OMITTED, on the same measured ground as stats.ts: the file is
    // dense, so absence carries no information the value does not. The one
    // exception is below.
    const stats: Record<string, number> = {};
    for (const [ourKey, column] of DIRECT) {
      const v = num(row, column);
      if (v !== null && v !== 0) stats[ourKey] = v;
    }
    for (const [ourKey, columns] of SUMMED) {
      const parts = columns.map((c) => num(row, c)).filter((v): v is number => v !== null);
      if (parts.length === 0) continue;
      const total = parts.reduce((a, b) => a + b, 0);
      if (total !== 0) stats[ourKey] = total;
    }

    // Yards allowed is the opponent's net yardage. Written even when zero,
    // because it is a TIERED stat: a defense that allowed nothing scores the
    // top band, and an absent key would score nothing at all.
    const allowed =
      gameId !== null && opponent !== null ? netYards.get(`${gameId}|${opponent}`) : undefined;
    if (allowed !== undefined) stats['dst_yds_allowed'] = allowed;

    assertTeam(team);
    const refs: CandidateFact['refs'] = [{ namespace: NS.team, key: team, role: 'subject' }];
    if (opponent !== null) {
      assertTeam(opponent);
      refs.push({ namespace: NS.team, key: opponent, role: 'participant' });
    }

    facts.push({
      kind: 'team.stat.line',
      key: factKey,
      // True when the game was played; the capture is the honest claim, and a
      // Tuesday correction supersedes rather than overwrites (as stats.ts).
      validFrom: opts.observedAt,
      validTo: null,
      refs,
      topics: ['performance'],
      confidence: 'official',
      payload: {
        teamKey: team,
        season,
        period,
        seasonType,
        opponent,
        gameId,
        stats,
        // nflverse prices no team-defense scheme, and the list says so.
        sourcePoints: [],
      },
    });
  }

  return {
    entities: [...entities.values()],
    facts,
    unmapped: [],
    // A week's file is the whole of that week for every club.
    complete: [...weeks].map((prefix) => ({ kind: 'team.stat.line', prefix })),
    skipped: [...skipped.entries()].map(([reason, count]) => ({ reason, count })),
  };
}
